import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
    TextField,
    InputAdornment
} from '@mui/material';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

const SEARCH_THRESHOLD = 8;

const FilterSection = ({
    title,
    options,
    selected,
    onChange,
    defaultExpanded = false
}) => {
    const [query, setQuery] = React.useState('');
    const safeSelected = Array.isArray(selected) ? selected : [];
    const activeCount = safeSelected.length;

    const filteredOptions = React.useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleToggle = (value) => {
        const exists = safeSelected.includes(value);
        const next = exists
            ? safeSelected.filter((v) => v !== value)
            : [...safeSelected, value];
        onChange(next);
    };

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
                bgcolor: 'transparent',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={<FiChevronDown size={16} />}
                sx={{ px: 1.5, minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                        {title}
                    </Typography>
                    {activeCount > 0 && (
                        <Box
                            sx={{
                                px: 0.75,
                                minWidth: 18,
                                height: 18,
                                borderRadius: 9,
                                bgcolor: '#1a1a2e',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {activeCount}
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                {options.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', py: 1 }}>
                        Nenhuma opção disponível
                    </Typography>
                ) : (
                    <>
                        {options.length > SEARCH_THRESHOLD && (
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Buscar..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiSearch size={14} />
                                        </InputAdornment>
                                    ),
                                    sx: { fontSize: 12 }
                                }}
                                sx={{ mb: 1 }}
                            />
                        )}
                        <Box
                            sx={{
                                maxHeight: 200,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {filteredOptions.map((opt) => {
                                const checked = safeSelected.includes(opt.value);
                                return (
                                    <FormControlLabel
                                        key={opt.value}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onChange={() => handleToggle(opt.value)}
                                                sx={{ py: 0.25 }}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography sx={{ fontSize: 12, flex: 1 }}>
                                                    {opt.label}
                                                </Typography>
                                                {opt.count !== undefined && (
                                                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                                        ({opt.count})
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ ml: 0, mr: 0 }}
                                    />
                                );
                            })}
                            {filteredOptions.length === 0 && (
                                <Typography sx={{ fontSize: 11, color: 'text.secondary', py: 0.5 }}>
                                    Nenhum resultado para "{query}"
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default FilterSection;
